import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import Vue, { nextTick } from 'vue'

import { useChatThreadStore } from './chatThreadStore'

import helpers from '@/logic/helpers'
import { relaunchApi } from '@/logic/relaunchApi'
import azureChat from '@/modules/chat/api/azureChat'
import store from '@/store'

// Persisted flag to remember if the user dismissed the chat push prompt.
const chatNotificationPromptDismissedRef = useLocalStorage('chatNotificationPromptDismissed', false)

// Persisted list of recently used reaction emojis (global across all threads)
const recentReactionEmojisRef = useLocalStorage('recentReactionEmojis', ['👀', '🥳', '😄'])

export const useChatStore = defineStore('chatStore', {
  state: () => ({
    myChatThreads: [],
    subscribedGroups: {},
    openAzureChatThreadId: undefined,
    chatOpened: false,
    chatThread: null,
    userDetails: null,
    usersSearchQuery: '',
    chatSearchQuery: null,
    usersSearchResult: [],
    isSearchingUsers: false,
    colleaguesAreLoading: false,
    randomColleaguesAreLoading: false,
    colleagues: [],
    isDragging: false,
    fileUploadRef: null,
    fileUploadKey: new Date().valueOf(),
    threadsHasUnreadMessages: {},
    imageToShowUrl: null,
    isImageModalOpen: false,
    usersListLoaded: false,
    isInitialLoading: true,
    chatNotificationPromptDismissed: chatNotificationPromptDismissedRef.value,
    shouldShowNotificationPrompt: false,
    resortThreads: false,
    sortedThreads: [],
    recentReactionEmojis: recentReactionEmojisRef.value,
    hiddenChatButtonSources: [],
    // Track which message's emoji picker is open (only one at a time)
    openEmojiPickerMessageId: null,
  }),
  getters: {
    usersList() {
      if (this.resortThreads) {
        this.sortedThreads = this.myChatThreads.filter((thread) => thread.participant).sort((threadA, threadB) => {
          const hasUnreadA = this.threadsHasUnreadMessages[threadA.azure_chat_thread_id]
          const hasUnreadB = this.threadsHasUnreadMessages[threadB.azure_chat_thread_id]

          if (hasUnreadA && !hasUnreadB) {
            return -1
          }
          if (!hasUnreadA && hasUnreadB) {
            return 1
          }

          const lastMessageACreatedDate = threadA.participant.lastMessage?.created_at
          const lastMessageBCreatedDate = threadB.participant.lastMessage?.created_at
          if (!lastMessageACreatedDate && !lastMessageBCreatedDate) {
            return 0
          }
          if (!lastMessageACreatedDate) {
            return 1
          }
          if (!lastMessageBCreatedDate) {
            return -1
          }

          return new Date(lastMessageBCreatedDate).getTime() - new Date(lastMessageACreatedDate).getTime()
        }).map((thread) => thread.participant)
        this.resortThreads = false
      }

      return this.sortedThreads
    },
    hasNewUnseenMessages() {
      return this.chatThread && this.threadsHasUnreadMessages[this.chatThread.azure_chat_thread_id]
    },
    unreadThreadCount() {
      return Object.values(this.threadsHasUnreadMessages).filter((hasUnread) => hasUnread).length
    },
    isChatButtonHidden() {
      return this.hiddenChatButtonSources.length > 0
    },
    /**
     * Get the 4 quick reaction emojis: 👍 followed by 3 most recently used
     * @returns {Array} Array of 4 emoji strings
     */
    quickReactionEmojis() {
      return ['👍', ...this.recentReactionEmojis]
    },
  },
  actions: {
    hideChatButton(source) {
      if (!source || this.hiddenChatButtonSources.includes(source)) {
        return
      }
      this.hiddenChatButtonSources.push(source)
      this.chatOpened = false
    },
    showChatButton(source) {
      this.hiddenChatButtonSources = this.hiddenChatButtonSources.filter((hiddenSource) => hiddenSource !== source)
    },
    triggerNotificationPromptAfterSend() {
      this.shouldShowNotificationPrompt = true
    },
    hideNotificationPrompt(persistDismissal = false) {
      this.shouldShowNotificationPrompt = false
      if (persistDismissal) {
        chatNotificationPromptDismissedRef.value = true
      }
      this.chatNotificationPromptDismissed = true
    },
    setThreadAutoTranslate(azureChatThreadId, enabled) {
      if (!azureChatThreadId) {
        return
      }
      const threadIndex = this.myChatThreads.findIndex((currentThread) => currentThread.azure_chat_thread_id === azureChatThreadId)
      if (threadIndex !== -1) {
        const updatedThread = {
          ...this.myChatThreads[threadIndex],
          auto_translate_enabled: !!enabled,
        }
        Vue.set(this.myChatThreads, threadIndex, updatedThread)
      }
      if (this.chatThread?.azure_chat_thread_id === azureChatThreadId) {
        Vue.set(this.chatThread, 'auto_translate_enabled', !!enabled)
      }
    },
    async fetchMyChatThreads() {
      const response = await relaunchApi.get('/chat/threads')

      // Only add new threads to the list to prevent multiple api calls and unnecessary re-sorting.
      const responseThreadIds = new Set(response.data.map((t) => t.azure_chat_thread_id))
      const existingThreadIds = new Set(this.myChatThreads.map((t) => t.azure_chat_thread_id))
      const newThreads = response.data
        .filter((thread) => !existingThreadIds.has(thread.azure_chat_thread_id))
        .map((thread) => {
          const participant = thread.participant
          participant.threadId = thread.id
          participant.lastMessage = thread.message
          participant.azure_chat_thread_id = thread.azure_chat_thread_id
          participant.hasUnreadMessages = false
          return { ...thread, participant }
        })

      // Remove threads that are no longer returned by the API (in-place to avoid replacing the array reference)
      let removed = false
      for (let index = this.myChatThreads.length - 1; index >= 0; index--) {
        const thread = this.myChatThreads[index]
        if (!responseThreadIds.has(thread.azure_chat_thread_id)) {
          this.myChatThreads.splice(index, 1)
          removed = true
        }
      }

      if (newThreads.length || removed) {
        this.resortThreads = true
        this.myChatThreads.push(...newThreads)
      }
    },
    async fetchLastMessages() {
      // fetch last messages which are saved in MongoDB
      const response = await relaunchApi.get('/chat/threads/last-messages')

      this.myChatThreads = this.myChatThreads.map((thread) => {
        const message = response.data.find((item) => item.azure_chat_thread_id === thread.azure_chat_thread_id)

        if (message) {
          message.created_at = new Date(message.created_at)
          thread.participant.lastMessage = message
          return { ...thread, message }
        }

        return thread
      })

      const azureChatThreads = await azureChat.getChatThreads()
      // fetch the newest messages from Azure if messages from MongoDB aren't up to date
      this.myChatThreads = await Promise.all(
        this.myChatThreads.map(async (thread) => {
          const foundAzureChatThread = azureChatThreads.find((azureChatThread) => thread.azure_chat_thread_id === azureChatThread.id)
          if (!thread.message || (foundAzureChatThread && foundAzureChatThread.lastMessageReceivedOn > thread.message.created_at)) {
            const participant = thread.participant

            const chatThreadStore = useChatThreadStore(thread.azure_chat_thread_id)
            participant.lastMessage = await chatThreadStore.getLastMessage()

            return { ...thread, participant }
          }

          return thread
        }),
      )
      this.resortThreads = true
    },
    async setChatThreadLastMessage(azureChatThreadId) {
      const chatThreadStore = useChatThreadStore(azureChatThreadId)
      const thread = this.myChatThreads.find((chatThread) => chatThread.azure_chat_thread_id === azureChatThreadId)
      thread.participant.lastMessage = await chatThreadStore.getLastMessage()
      await nextTick()
      this.resortThreads = true
    },

    async updateHasUnreadMessagesInThreads(chatThreadId = null) {
      let threads = [...this.myChatThreads]
      if (chatThreadId) {
        threads = threads.filter((thread) => thread.azure_chat_thread_id === chatThreadId)
      }
      const threadsHasUnreadMessages = { ...this.threadsHasUnreadMessages }

      await Promise.all(threads.map(async (thread) => {
        const chatThreadStore = useChatThreadStore(thread.azure_chat_thread_id)
        try {
          await chatThreadStore.loadReadReceipts()
        } catch (error) {
          console.error('Failed to load read receipts:', error)
        }
        const lastMessage = await chatThreadStore.getLastMessage()

        // Mark thread as unread if:
        // 1. First message scenario: No read receipt exists (new thread with first message)
        // 2. Unread message scenario: Read receipt exists but is older than the last message
        // 3. Already read scenario: Read receipt is newer than or equal to last message (marked as read)
        threadsHasUnreadMessages[thread.azure_chat_thread_id] = lastMessage
          && !this.isMe(lastMessage.azure_sender_id)
          && (!chatThreadStore.myReadReceipt || +chatThreadStore.myReadReceipt.chatMessageId < +lastMessage.azure_message_id)
      }))
      Vue.set(this, 'threadsHasUnreadMessages', threadsHasUnreadMessages)
    },
    markThreadAsRead(chatThreadId) {
      const threadsHasUnreadMessages = { ...this.threadsHasUnreadMessages }
      threadsHasUnreadMessages[chatThreadId] = false
      Vue.set(this, 'threadsHasUnreadMessages', threadsHasUnreadMessages)
    },
    async searchUsers() {
      if (this.usersSearchQuery.length < 3) {
        this.usersSearchResult = []
        this.colleaguesAreLoading = false
        return
      }
      this.isSearchingUsers = true
      this.colleaguesAreLoading = false
      const spinnerTimeout = setTimeout(() => {
        this.colleaguesAreLoading = true
      }, 100)
      try {
        const response = await relaunchApi.post('/communities/users/search', { query: this.usersSearchQuery })
        this.usersSearchResult = response.data
      } catch (error) {
        this.usersSearchResult = []
      } finally {
        this.isSearchingUsers = false
        clearTimeout(spinnerTimeout)
        this.colleaguesAreLoading = false
      }
    },
    async fetchRandomUsers() {
      this.randomColleaguesAreLoading = true
      try {
        const response = await relaunchApi.get('/chat/users/random')
        this.colleagues = response.data
      } catch (error) {
        this.colleagues = []
      } finally {
        this.randomColleaguesAreLoading = false
      }
    },
    isMe(userId) {
      return parseInt(userId) === store.getters['auth/userId']
    },
    updateMessagesContainerHeight(isTyping = false) {
      const messagesContainer = document.querySelector('.c-chat__dialogMainContentWrapper')
      if (!messagesContainer) {
        return
      }
      let containerHeight = messagesContainer.getBoundingClientRect().height

      if (isTyping) {
        const typingIndicator = document.querySelector('.c-chat__isTyping')
        if (typingIndicator) {
          containerHeight -= typingIndicator.getBoundingClientRect().height
        }
      }

      const messagesScroller = document.querySelector('.c-chat__messagesScroller')
      if (messagesScroller) {
        messagesScroller.style.height = `${containerHeight}px`
      }
    },
    closeChatWhenNavigatingInMobile() {
      if (helpers.isMobileViewport()) {
        if (this.chatOpened) {
          this.chatOpened = false
        }
      }
    },
    /**
     * Update the recently used reaction emojis list
     * Uses push-left mechanism: new emoji goes to front, oldest drops off
     * @param {string} emoji - The emoji to add to recent list
     */
    updateRecentReactionEmojis(emoji) {
      // 👍 is always in slot 1, not stored in recent list
      if (emoji === '👍') {
        return
      }

      // Remove if already present (to avoid duplicates)
      const filtered = this.recentReactionEmojis.filter((existingEmoji) => existingEmoji !== emoji)

      // Add to front
      filtered.unshift(emoji)

      // Keep only 3
      this.recentReactionEmojis = filtered.slice(0, 3)

      // Persist to localStorage
      recentReactionEmojisRef.value = this.recentReactionEmojis
    },
  },
})
